/* eslint-disable max-len */
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { Divider } from '@huspy/styled-system/jsx';
import { Image, Accordion } from '@mantine/core';
import { NotFoundComponent } from '@modules/core/components/NotFoundComponent';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { imagesPath } from '@shared/css.const';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { CreditsRoute } from '@modules/core/presentation/v2/routes';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useEffect } from 'react';

const fqs = [
  {
    value: 'What is a credit?',
    description:
      'Credits are the currency used within the Huspy Broker Portal to access specific features. Credits can be earned by submitting cases to Huspy, and used on different features such as direct-to-bank submissions and generating proposals. When purchased, each credit costs 10 AED.',
  },
  {
    value: 'Why introduce the credit system?',
    description:
      'In order to make it possible for Huspy to continue developing the Broker Portal and create more value for brokers, we have introduced and earn-and-use pricing model which rewards brokers who submit cases to Huspy.',
  },
  {
    value: 'I give Huspy business. Why should I pay?',
    description:
      'The credit system is built in a way which rewards brokers who submit business to Huspy. Each time you submit a case to Huspy and the submission goes through to the bank, you earn credits which you can use for other features. Brokers who submit a lot of cases to Huspy should earn enough credits to cover their usage of other features in the platform, and therefore in general will not have to pay for credits.',
  },
  {
    value: 'How much does a credit cost?',
    description:
      'Each credit costs 10AED, with a minimum purchase of 10 credits. Credits can be purchased at discounted rates when buying high volumes.',
  },
  {
    value: 'What features can I earn credits on?',
    description:
      'You can earn credits when submitting cases to Huspy. 10 credits will be added to your account upon the case successfully being submitted to the first bank.',
  },
  {
    value: 'What features can I spend credits on?',
    description:
      'You can spend credits on generating proposals, submitting cases directly to banks through your own agreements, and submitting additional bank applications to Huspy. For more details, scroll up to the "Using credits" section of this page.',
  },
  {
    value: 'What happens if I run out of credit?',
    description:
      'If your balance reaches a sufficiently low amount, you will required to purchase more credits before using the features that require credits. Purchased credits will reflect instantly on your account.',
  },
  {
    value: 'What happens if I have issues with credits?',
    description:
      'If you see any discrepancies in the amount of credits used or face any other issues with credits, please submit a support ticket to ',
  },
  {
    value: 'Can I get a refund on unused credits? ',
    description: 'Credits are non-refundable once purchased.',
  }
];

const FrequentlyAskedQuestions = () => {
  const fqsItem = fqs.map((item) => (
    <Accordion.Item key={ item.value } value={ item.value }>
      <Accordion.Control>{item.value}</Accordion.Control>
      <Accordion.Panel>{item.description}</Accordion.Panel>
    </Accordion.Item>
  ));

  return (
    <Flex direction='column' gap='4'>
      <Text size='3xl' fontWeight='bold'>
        Frequently Asked Questions
      </Text>
      <Accordion>{fqsItem}</Accordion>
    </Flex>
  );
};

const WhatsCredit = () => (
  <Flex direction='column' gap='4'>
    <Text size='3xl' fontWeight='bold'>
      What are credits?
    </Text>
    <Text size='md' color='neutral.500'>
      Credits are the currency used within the Huspy Broker Portal to access
      specific features. Credits can be earned by submitting cases to Huspy, and
      used on different features such as direct-to-bank submissions and
      generating proposals. When purchased, each credit costs 10 AED.
    </Text>
    <Image
      src={ `${imagesPath}/credits/screenshot-1.png` }
      w='660px'
      h='fit-content'
    />
  </Flex>
);

const EaringCredits = () => (
  <Flex direction='column' gap='4'>
    <Box>
      <Text size='3xl' fontWeight='bold'>
        Earning Credits
      </Text>
      <Text size='md' color='neutral.500'>
        To reward brokers who do business with Huspy, we allow you to earn
        credits when submitting cases to Huspy. These are credited to your
        account upon the case successfully being submitted to the first bank.
      </Text>
    </Box>
    <Text size='md' color='neutral.500'>
      Earn 10 credits for your first bank submission to Huspy on a case
    </Text>
    <Text size='md' color='neutral.500'>
      Example: If you submit a case to Huspy with ADIB and CBD selected as the
      banks, you will earn 10 credits once Huspy submits the case to ADIB. You
      will not earn more credits when Huspy submits the case to CBD.
    </Text>
    <Image
      src={ `${imagesPath}/credits/screenshot-2.png` }
      w='660px'
      h='fit-content'
    />
  </Flex>
);

const UsingCredits = () => (
  <Flex direction='column' gap='4'>
    <Box>
      <Text size='3xl' fontWeight='bold'>
        Using Credits
      </Text>
      <Text size='md' color='neutral.500'>
        Credits can be used in the Broker Portal to access valuable features
        which save you time and hassle when submitting cases. You can use
        credits to access the following features within the portal:
      </Text>
    </Box>
    <Box>
      <Text size='lg' fontWeight='bold'>
        Generating Proposals
      </Text>
      <Text size='md' color='neutral.500'>
        Each time you generate a proposal, credits will be used
      </Text>
      <ul style={ { listStyleType: 'square', marginLeft: '24px' } }>
        <li>
          <Text size='md' color='neutral.500'>
            Cost: 1 credit per proposal generated
          </Text>
        </li>
        <li>
          <Text size='md' color='neutral.500'>
            Example: if you generate 5 proposals, 5 credits will be deducted
            from your balance.
          </Text>
        </li>
      </ul>
    </Box>
    <Box>
      <Text size='lg' fontWeight='bold'>
        Submitting additional bank applications
      </Text>
      <Text size='md' color='neutral.500'>
        Credits will be used when you submit more than two bank applications on
        a case sent to Huspy.
      </Text>
      <ul style={ { listStyleType: 'square', marginLeft: '24px' } }>
        <li>
          <Text size='md' color='neutral.500'>
            Cost: 20 credits per extra application after the first two bank
            applications
          </Text>
        </li>
        <li>
          <Text size='md' color='neutral.500'>
            Example: if you submit a case with ADIB as the primary bank, CBD as
            the backup and ENBD as the additional, you will be charged 20
            credits for the ENBD application. If you then submit Mashreq as a
            fourth bank application, you will be charged another 20 credits.
          </Text>
        </li>
      </ul>
    </Box>
    <Box>
      <Text size='lg' fontWeight='bold'>
        Direct-to-Bank Case Submissions
      </Text>
      <Text size='md' color='neutral.500'>
        To streamline the process of submitting cases directly to banks through
        your own agreements, Huspy allows you to auto-generate submission emails
        with the click of a button. When submitting a case direct-to-bank,
        credits will be used:.
      </Text>
      <ul style={ { listStyleType: 'square', marginLeft: '24px' } }>
        <li>
          <Text size='md' color='neutral.500'>
            Cost: 20 credits for up to 3 direct-to-bank submissions on a case
          </Text>
        </li>
        <li>
          <Text size='md' color='neutral.500'>
            Example: if you submit directly to ADIB, CBD and ENBD, you will be
            charged 20 credits
          </Text>
        </li>
      </ul>
    </Box>
  </Flex>
);

const PurchasingCredits = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const clickOnGetCredits = () => {
    navigate({ to: CreditsRoute.to });
  };

  return (
    <Flex direction='column' gap='4'>
      <Box>
        <Text size='3xl' fontWeight='bold'>
          Purchasing credits
        </Text>
        <Text size='md' color='neutral.500'>
          To purchase credits, follow these steps:
        </Text>
      </Box>
      <Box>
        <ul style={ { listStyleType: 'square', marginLeft: '24px' } }>
          <li>
            <Text size='md' color='neutral.500'>
              Log in to the Huspy Broker Portal.
            </Text>
          </li>
          <li>
            <Text size='md' color='neutral.500'>
              Click your brokerage’s name in the side menu
            </Text>
          </li>
          <li>
            <Text size='md' color='neutral.500'>
              Under Account, click Credits
            </Text>
          </li>
          <li>
            <Text size='md' color='neutral.500'>
              Select Get more credits
            </Text>
          </li>
          <li>
            <Text size='md' color='neutral.500'>
              Choose the number of credits you&apos;d like to purchase
            </Text>
          </li>
          <li>
            <Text size='md' color='neutral.500'>
              Complete the payment process, and your credits will be added to
              your account.
            </Text>
          </li>
        </ul>
      </Box>
      <Image
        src={ `${imagesPath}/credits/screenshot-1.png` }
        w='660px'
        h='fit-content'
      />
      <Flex align='center'>
        <Button variant='primary' size='md' onClick={ clickOnGetCredits }>
          {t('core.credits.getMoreCredits')}
        </Button>
      </Flex>
    </Flex>
  );
};

export const CreditsInfoPage = () => {
  useEffect(() => {
    if (!IS_SPAIN_ENV) {
      trackAmplitudeEvent(USER_EVENTS.CREDITS.LEARN_MORE_PAGE_VIEWED);
    }
  }, []);

  if (IS_SPAIN_ENV) {
    return <NotFoundComponent />;
  }

  return (
    <Flex w='100%' justify='center' align='center'>
      <Flex direction='column' gap='10' w='660px'>
        <Image src={ `${imagesPath}/credits/banner.png` } w='660px' h='175px' />
        <Flex direction='column' gap='3'>
          <Text size='md' color='neutral.500'>
            Last updated: December 20, 2024
          </Text>
          <Text size='md' color='neutral.500'>
            Huspy is moving to an earn-and-use pricing model for the Broker
            Portal. The model ensures you can continue to use the product for free
            by earning credits as you submit cases to Huspy. Credits will be used
            when accessing specific features such as proposal generation.
          </Text>
          <Text size='md' color='neutral.500'>
            Important note: gifted credits can be expired at Huspy&apos;s discretion with a minimum notice of fifteen (15) days.
          </Text>
        </Flex>
        <WhatsCredit />
        <Divider />
        <EaringCredits />
        <Divider />
        <UsingCredits />
        <Divider />
        <PurchasingCredits />
        <Divider />
        <FrequentlyAskedQuestions />
      </Flex>
    </Flex>
  );
};
